// Customizer const
export const LIGHT_THEME = 'light'
export const DARK_THEME = 'dark'
export type TeamsThemeType = 'default' | 'dark' | 'contrast'

// Brand palette - VT Brand Book V1 (palette p18). Single source of truth for brand colour.
export const brandColors = {
  primary: '#7F00FF', // Violet
  primaryBg: '#E6DAFC', // Lavender tint for selected/hover surfaces - 4.72:1 under #7F00FF text (AA needs 4.5:1)
  primaryDarkAccent: '#D1A3FF', // dark-mode tint of primary (see note in darkThemeToken)

  aubergine: '#281853', // Dark Violet - darkest text / dark-mode base surface
  brightGray: '#EBEBEB', // light brand surface / divider
  whiteCoffee: '#E7E1D3', // warm brand surface

  // Shades Ant derives from primary at runtime. Transcribed so LESS can reference them
  // too - regenerate with theme.getDesignToken() if primary ever changes.
  primaryHover: '#9B29FF',
  primaryActive: '#6500D9',
  primaryBorder: '#CA7AFF',
  primaryBorderHover: '#B452FF',
  primaryBgHover: '#DDA3FF',

  // Decorative only - never use as state colours
  vividOrange: '#ED6E44',
  peachOrange: '#F0CA99',
  metallicSunburst: '#997036',
} as const

// Functional neutrals - Ant Design v5's ramp. The brand book defines only surface greys,
// so text/border/fill levels come from Ant (§2 decision log). Text and fill are alpha
// blacks, matching what Ant emits, so our CSS agrees with Ant's component internals.
export const neutralColors = {
  text: 'rgba(0, 0, 0, 0.88)',
  textSecondary: 'rgba(0, 0, 0, 0.65)',
  textTertiary: 'rgba(0, 0, 0, 0.45)',
  textQuaternary: 'rgba(0, 0, 0, 0.25)',

  border: '#d9d9d9',
  borderSecondary: '#f0f0f0',

  fill: 'rgba(0, 0, 0, 0.15)',
  fillSecondary: 'rgba(0, 0, 0, 0.06)',
  fillTertiary: 'rgba(0, 0, 0, 0.04)',
  fillQuaternary: 'rgba(0, 0, 0, 0.02)',

  bgContainer: '#ffffff',
  bgLayout: '#f5f5f5',
} as const

// Spacing - Ant Design's size scale (4px base), extended with 40/64 for layout.
// Use these in inline style objects; .less files use @vt-size-* from
// @vacationtracker/shared/styles/tokens.
export const spacing = {
  xxs: 4,
  xs: 8,
  sm: 12,
  base: 16,
  md: 20,
  lg: 24,
  xl: 32,
  xxl: 48,
  layout: 40,
  layoutLg: 64,
} as const

export const radius = {
  sm: 4,
  base: 8,
  lg: 12,
  full: 9999,
} as const

export const fontSize = {
  sm: 12,
  base: 14,
  lg: 16,
  h5: 16,
  h4: 20,
  h3: 24,
  h2: 30,
  h1: 38,
  /**
   * The page title, which is an h1 but not at h1's size. `h1` is antd's display scale and this
   * product has no page that wants 38px above a settings form; the two dashboards that render an
   * h1 already override it. Its own step rather than h4, because a page title is a role, not a
   * size someone picked.
   */
  pageTitle: 22,
} as const

export const fontWeight = {
  regular: 400,
  /**
   * A name that leads a record or a group, such as a location card's name or a country heading: a step
   * above body text without reading as bold. `medium` already means 600, so this is named by its role.
   */
  title: 500,
  medium: 600,
  bold: 700,
} as const

export const lineHeight = {
  base: 1.5,
  heading: 1.2,
} as const

export const shadows = {
  none: 'none',
  sm: '0 1px 2px rgba(0, 0, 0, 0.06)',
  base: '0 2px 8px rgba(0, 0, 0, 0.1)',
  lg: '0 8px 24px rgba(0, 0, 0, 0.12)',
} as const

// The spacing and type scales below already equal antd 5.29's computed defaults.
// They are set explicitly so the scale is visible in our own source and an antd
// upgrade cannot move it without the change showing up in this file's diff.
const scaleTokens = {
  sizeXXS: spacing.xxs,
  sizeXS: spacing.xs,
  sizeSM: spacing.sm,
  size: spacing.base,
  sizeMD: spacing.md,
  sizeLG: spacing.lg,
  sizeXL: spacing.xl,
  sizeXXL: spacing.xxl,

  fontSizeSM: fontSize.sm,
  fontSize: fontSize.base,
  fontSizeLG: fontSize.lg,
  fontSizeHeading1: fontSize.h1,
  fontSizeHeading2: fontSize.h2,
  fontSizeHeading3: fontSize.h3,
  fontSizeHeading4: fontSize.h4,
  fontSizeHeading5: fontSize.h5,
  fontWeightStrong: fontWeight.medium,
} as const

// Unlike scaleTokens above, these three differ from antd's defaults and so restyle
// every antd component: borderRadius 6 -> 8, lineHeight 1.5714 -> 1.5, and a four-level
// shadow scale in place of antd's. Locked in Phase 2 sections 4-6.
const elevationTokens = {
  borderRadius: radius.base,
  borderRadiusSM: radius.sm,
  borderRadiusLG: radius.lg,
  lineHeight: lineHeight.base,
  boxShadow: shadows.base,
  boxShadowSecondary: shadows.lg,
  boxShadowTertiary: shadows.sm,
} as const

// Semantic states - Ant Design v5 defaults. The brand book defines no green or red, so
// success/error come from Ant. Warning stays Ant gold for caution-vs-danger clarity;
// brand Vivid Orange is decorative and must not be used as a state colour.
// Mirrors shared/styles/default-ant5-variables.less (@success-color etc.) so .tsx inline
// styles and .less resolve to the same values. Bg/Border/Active/Hover are Ant's own
// derived shades, pinned here rather than computed, so LESS and .tsx can reference them too.
export const semanticColors = {
  colorSuccess: '#52c41a',
  colorSuccessBg: '#f6ffed',
  colorSuccessBorder: '#b7eb8f',
  colorSuccessActive: '#389e0d',

  colorWarning: '#faad14',
  colorWarningBg: '#fffbe6',
  colorWarningBorder: '#ffe58f',
  colorWarningActive: '#d48806',

  colorError: '#ff4d4f',
  colorErrorBg: '#fff2f0',
  colorErrorBorder: '#ffccc7',
  colorErrorHover: '#ff7875',
  colorErrorActive: '#d9363e',
} as const

// Ant Design v5 theme customization
export const lightThemeToken = {
  token: {
    colorPrimary: brandColors.primary,
    colorLink: brandColors.primary,
    colorInfo: brandColors.primary,
    colorPrimaryBg: brandColors.primaryBg,
    ...semanticColors,
    colorSuccessText: '#237804',
    colorWarningText: '#874D00',
    colorErrorText: '#A8071A',
    ...scaleTokens,
    ...elevationTokens,
    fontFamily: 'NoirPro, sans-serif',
  },
  components: {
    Dropdown: {
      zIndexPopup: 10000,
    },
    Select: {
      // Tags one step lighter than antd's default (colorFillSecondary) - selected
      // chips read quieter in multi-selects.
      multipleItemBg: neutralColors.fillTertiary,
      // Selected options match the FilterTrigger panel: regular weight, primary text
      // on the tint (4.72:1) - antd's 600 bold is the odd one out in this family.
      optionSelectedFontWeight: fontWeight.regular,
      optionSelectedColor: brandColors.primary,
    },
    Statistic: {
      contentFontSize: 18,
    },
  },
  inherit: true,
}

export const darkThemeToken = {
  token: {
    // Dark mode uses a lighter tint of brand for text/links/borders: full-strength
    // #7F00FF scores 2.55:1 on #1C222B (WCAG fail); the tint scores 7.92:1.
    // Filled buttons below keep full-strength brand - white on #7F00FF is 6.28:1.
    colorPrimary: brandColors.primaryDarkAccent,
    colorInfo: brandColors.primaryDarkAccent,
    colorTextBase: '#F0F3F9',
    colorBgBase: '#1C222B',
    colorBgContainer: '#252B35',
    colorBorder: '#495862',
    colorBorderSecondary: '#495862',
    // This app swaps whole token objects instead of antd's dark algorithm, so the primary-tinted
    // surfaces must be stated: derived from colorPrimary they come out near-white on dark.
    colorPrimaryBg: 'rgba(127, 0, 255, 0.22)',
    colorPrimaryBgHover: 'rgba(127, 0, 255, 0.32)',
    colorPrimaryBorder: 'rgba(209, 163, 255, 0.5)',
    colorPrimaryBorderHover: 'rgba(209, 163, 255, 0.7)',
    // Same for the semantic tints: derived from the seed colors they come out near-white on dark.
    colorSuccessBg: 'rgba(82, 196, 26, 0.12)',
    colorSuccessBorder: 'rgba(82, 196, 26, 0.3)',
    colorWarningBg: 'rgba(250, 173, 20, 0.12)',
    colorWarningBorder: 'rgba(250, 173, 20, 0.3)',
    ...scaleTokens,
    ...elevationTokens,
    fontFamily: 'NoirPro, sans-serif',
  },
  components: {
    Button: {
      colorBgContainer: '#252B35',
      // Primary buttons - full-strength brand (white text on it is 6.28:1)
      colorPrimary: brandColors.primary,
      colorPrimaryHover: '#a855ff', // Lighter for hover
      colorPrimaryActive: '#7c1dff', // Darker for active/pressed
      colorPrimaryBorder: '#8f2aff', // Match primary

      // Danger buttons - Remove box shadow
      colorError: '#ff4d4f', // Standard danger color
      colorErrorHover: '#ff7875', // Lighter for hover
      colorErrorActive: '#d9363e', // Darker for active
      colorErrorBorder: '#ff4d4f', // Match danger color

      // Remove all box shadows
      boxShadow: 'none',
      primaryShadow: 'none',
      dangerShadow: 'none',
      defaultShadow: 'none',

      // Default buttons - Secondary colors
      defaultBg: '#2D3748', // dark-theme-secondary-color
      defaultColor: '#F0F3F9', // Light text
      defaultBorderColor: '#495862', // Dark border
      defaultHoverBg: '#374151', // Slightly lighter on hover
      defaultHoverColor: '#F0F3F9', // Keep text light
      defaultHoverBorderColor: '#6B7280', // Lighter border on hover
    },
    Input: {
      colorBorder: '#495862', // Dark theme border
      colorPrimaryHover: '#8f2aff', // Use stronger purple for focus
    },
    Select: {
      colorBorder: '#495862', // Dark theme border
      colorPrimaryHover: '#8f2aff', // Use stronger purple for focus
      optionSelectedFontWeight: fontWeight.regular,
    },
    Table: {
      colorBgContainer: '#252B35', // Match main-content wrapper background
      headerBg: '#252B35', // Table header background
      bodySortBg: '#252B35', // Sorted column background
      headerSortActiveBg: '#2D3748', // Active sort header background
      headerSortHoverBg: '#2D3748', // Hover sort header background
      rowHoverBg: '#2D3748', // Row hover background
      rowSelectedBg: '#2D3748', // Selected row background
      rowSelectedHoverBg: '#374151', // Selected row hover background
      colorBorder: '#495862', // Table border color
      colorSplit: '#495862', // Table split line color
    },
    Statistic: {
      contentFontSize: 18,
    },
    Dropdown: {
      zIndexPopup: 10000,
    },
  },
  inherit: true,
}
